/**
 * Os resultados da campanha no portal externo e os três interruptores da 5.2 (§5.3, §5.5, §5.6 e §5.8 do plano do
 * pacote Campanhas + DV360; P-11, P-46 a P-50; DS-673 a DS-676). Contrato próprio, fora de `campanhas.ts` e de
 * `dv360.ts`, para a etapa do portal não disputar o arquivo da campanha nem o do DV360 com as outras frentes.
 *
 * Rotas internas (API, sob a campanha; todas com a entidade ativa e o 404 de sempre para a campanha de outra
 * entidade):
 *   GET  /campanhas/:id/portal            → `PortalDaCampanha` (o diálogo "Portal": prévia por público, vínculos,
 *                                            aviso de parcial, capacidades) — `midia · ver`
 *   POST /campanhas/:id/portal/ligar      → 201 `PortalDaCampanha` (corpo `InterruptorDoPortal`) — `midia · editar_todos`
 *   POST /campanhas/:id/portal/desligar   → 201 `PortalDaCampanha` (corpo `InterruptorDoPortal`) — `midia · editar_todos`
 *
 * Rotas externas (portal-api; contrato em `portal-externo.ts`):
 *   GET /portal/v1/cliente/campanhas      → cada cartão ganha `resultados` (null = não publicado para quem lê)
 *   GET /portal/v1/parceiro/campanhas     → `CampanhaDoParceiroNoPortal[]` (P-48: só com o interruptor do Parceiro)
 *
 * Um nível de detalhe único para os três públicos (§5.8): o interruptor decide QUEM vê, e a 5.2 diz, por público, as
 * empresas que recebem agora, pelas partes atuais do Pedido (CAMP-021, v1.9.0); os números são os mesmos. A conta
 * é a do domínio (`consolidarDv360`), a mesma do Resumo interno: `resultadosDoPortalPelaConsolidacao` é o ÚNICO lugar
 * que a aplica para o portal, e o job do worker (a projeção) e a prévia da API leem dele.
 */
import { z } from 'zod'
import {
  consolidarDv360,
  ctrEmPercentualDv360,
  dataNominal as comoDataNominal,
  DecimalDv360,
  SITUACOES_DA_CAMPANHA_NO_PORTAL,
  type ConsolidadoDv360,
  type FatoDv360,
  type VinculoParaConsolidarDv360,
} from '@dv/domain'
import { dataNominal, uuid } from './comum.js'

// ------------------------------------------------------------------------------------------------ o que o portal mostra

/** Os três públicos, na ordem do diálogo. O papel da parte no pedido decide qual interruptor vale para a empresa. */
export const PUBLICOS_DO_PORTAL = ['Cliente', 'Anunciante', 'Parceiro comissionado'] as const
export const publicoDoPortal = z.enum(PUBLICOS_DO_PORTAL)
export type PublicoDoPortal = z.infer<typeof publicoDoPortal>

/**
 * A situação dos dados (§5.8): `Completo` (a tela escreve "Completo até {resultadosAte}"), `Em atualização` (sem
 * números: escopo mudado, vínculo aguardando conferência, Campaign nova sem histórico ou cobertura parcial) e `Ainda sem
 * dados` (nenhuma Campaign vinculada). A parte do pedido não tira os números de ninguém (D-193 (7)): o público se resolve
 * ao vivo pelas partes, e quem fica continua vendo quando outra parte muda.
 */
export const SITUACOES_DOS_DADOS_NO_PORTAL = ['Completo', 'Em atualização', 'Ainda sem dados'] as const
export const situacaoDosDadosNoPortal = z.enum(SITUACOES_DOS_DADOS_NO_PORTAL)
export type SituacaoDosDadosNoPortal = z.infer<typeof situacaoDosDadosNoPortal>

/** O rótulo fixo ao lado dos números (§5.8, P-47), gravado na projeção e conferido pelo CHECK da 0200. */
export const ROTULO_DOS_RESULTADOS_NO_PORTAL =
  'Mídia programática das Campaigns vinculadas: não inclui outras mídias e não é distribuída por produto'

/** O texto da confirmação interna ao desligar (§5.3, §6 C3 Caches): o limite declarado da aba já aberta. */
export const TEXTO_AO_DESLIGAR_A_PUBLICACAO =
  'Novas consultas deixam de receber estes números agora. Quem está com a página aberta continua vendo o que já carregou até atualizar.'

const contagem = z.string().regex(/^\d{1,19}$/)
/** O CTR em percentual com duas casas, arredondado UMA vez pelo domínio (`ctrEmPercentualDv360`); null = "—". */
const percentualComDuasCasas = z.string().regex(/^\d{1,10}\.\d{2}$/)

/**
 * Os resultados de uma campanha, como o portal os entrega. Os números só existem no `Completo`; fora dele, os seis
 * campos de número vêm nulos. `atualizadoEm` é a última validação de relatório do conjunto (ISO 8601): envelhece quando
 * a atualização falha, e a última publicação completa fica (§6 C3 item 5).
 */
export const resultadosDaCampanhaNoPortal = z.object({
  situacaoDosDados: situacaoDosDadosNoPortal,
  impressoes: contagem.nullable(),
  cliques: contagem.nullable(),
  ctrPercentual: percentualComDuasCasas.nullable(),
  /** "Resultados de {primeiro dia com veiculação} a {X}"; o primeiro dia é nulo quando nada veiculou ainda. */
  resultadosDe: dataNominal.nullable(),
  resultadosAte: dataNominal.nullable(),
  atualizadoEm: z.string().nullable(),
  rotulo: z.literal(ROTULO_DOS_RESULTADOS_NO_PORTAL),
})
export type ResultadosDaCampanhaNoPortal = z.infer<typeof resultadosDaCampanhaNoPortal>

/** A situação da campanha no portal (P-11), as quatro frases. */
const situacaoDaCampanhaNoPortal = z.enum(SITUACOES_DA_CAMPANHA_NO_PORTAL)

/**
 * A campanha no bloco "Campanhas" do Portal do Parceiro comissionado (P-48): só com o interruptor do Parceiro ligado,
 * nos pedidos em que a empresa é parte, SEM o valor. Em Todas, uma vez por campanha; `empresaIds` são as empresas do
 * contexto que a alcançam. O cartão vem de `portal.parceiro_campanha` (0200): com o conjunto de vínculos mudado, a
 * campanha FICA e `resultados` diz "Em atualização", sem números (§6 C3 item 3); a publicação nova (religada, empresa
 * que entra) só aparece depois da materialização seguinte.
 */
export const campanhaDoParceiroNoPortal = z.object({
  campanhaId: uuid,
  codigo: z.string(),
  refExterna: z.string().nullable(),
  nome: z.string(),
  inicio: dataNominal,
  fim: dataNominal,
  situacao: situacaoDaCampanhaNoPortal,
  empresaIds: z.array(uuid).min(1),
  resultados: resultadosDaCampanhaNoPortal,
})
export type CampanhaDoParceiroNoPortal = z.infer<typeof campanhaDoParceiroNoPortal>

// ------------------------------------------------------------------------------------------------ a conta (uma só)

const idDoGoogle = z.string().regex(/^\d{1,19}$/)

/** Uma linha de `fn_portal_entrada_dos_resultados` (0200): o retrato que a consolidação do Resumo interno lê. */
export const entradaDosResultadosDoPortal = z.object({
  assinatura: z.string().min(1),
  vinculos: z.array(z.object({
    campaignId: idDoGoogle,
    estado: z.enum(['aguardando_conferencia', 'confirmado', 'nao_encontrado']),
    inicioCarga: dataNominal.nullable(),
    coletaEncerrada: z.boolean(),
    /** O "hoje" no fuso do Advertiser. */
    hoje: dataNominal,
    cobertura: z.array(z.object({ inicio: dataNominal, fim: dataNominal })),
  })),
  fatos: z.array(z.object({
    advertiserId: idDoGoogle,
    moeda: z.string().regex(/^[A-Z]{3}$/),
    campaignId: idDoGoogle,
    insertionOrderId: idDoGoogle,
    lineItemId: idDoGoogle,
    dia: dataNominal,
    custo: z.string().regex(/^-?\d{1,14}(?:\.\d{1,6})?$/),
    impressoes: contagem,
    cliques: contagem,
  })),
  /** A última validação de relatório das Campaigns do conjunto (ISO 8601), ou null. */
  atualizadoEm: z.string().nullable(),
})
export type EntradaDosResultadosDoPortal = z.infer<typeof entradaDosResultadosDoPortal>

/** O que a consolidação diz para o portal: os resultados e o que o materializador confere de novo no banco. */
export interface ResultadosCalculadosDoPortal {
  readonly resultados: ResultadosDaCampanhaNoPortal
  /** O consolidado do conjunto pretendido inteiro está completo (todos confirmados e cobertos). */
  readonly completo: boolean
  /** "parcial (k de n)": há vínculo e o consolidado não está completo. */
  readonly parcial: boolean
}

/**
 * A consolidação do portal: `consolidarDv360` (DS-662), a MESMA função do Resumo interno, sobre o retrato da 0200, no
 * mesmo escopo (os vínculos não anulados) e no mesmo intervalo (o acumulado, sem recorte: o gasto antes do início
 * planejado conta). O mapeamento do retrato é o do acompanhamento do gasto: sem início de carga gravado, o vínculo não
 * está coberto (cobertura vazia), e o início é o "hoje" do Advertiser. Exportada para a prova de equivalência com o
 * consolidado interno (X, k de n e o custo, na massa hostil; E8R-BT-I2): o portal nunca a mostra além dos resultados.
 */
export function consolidacaoDoPortal(entrada: EntradaDosResultadosDoPortal): ConsolidadoDv360 {
  const vinculos: VinculoParaConsolidarDv360[] = entrada.vinculos.map((v) => ({
    campaignId: v.campaignId,
    estado: v.estado,
    inicioDaCarga: comoDataNominal(v.inicioCarga ?? v.hoje),
    hoje: comoDataNominal(v.hoje),
    cobertura: v.inicioCarga === null ? [] : v.cobertura.map((b) => ({ inicio: comoDataNominal(b.inicio), fim: comoDataNominal(b.fim) })),
    coletaEncerrada: v.coletaEncerrada,
  }))
  const fatos: FatoDv360[] = entrada.fatos.map((f) => ({
    advertiserId: f.advertiserId,
    moeda: f.moeda,
    campaignId: f.campaignId,
    insertionOrderId: f.insertionOrderId,
    lineItemId: f.lineItemId,
    dia: comoDataNominal(f.dia),
    custo: new DecimalDv360(f.custo),
    impressoes: BigInt(f.impressoes),
    cliques: BigInt(f.cliques),
  }))
  return consolidarDv360({ vinculos, fatos })
}

/**
 * A conta do portal sobre a consolidação acima. Números só com o consolidado completo; sem vínculo, "Ainda sem dados";
 * fora disso, "Em atualização". A parte comercial do pedido não entra (D-193 (7)). O banco repete a decisão na fonte da
 * projeção, com a assinatura viva e os confirmados (0200): este resultado é a previsão exata do que a rodada grava — a
 * única exceção é o vínculo carimbado por uma imagem da v1.8.4 num recuo cuja parte mudou depois, que a fonte da 0204
 * ainda segura em "Em atualização" (DS-686 (h)); a 0206 tira essa regra.
 */
export function resultadosDoPortalPelaConsolidacao(entrada: EntradaDosResultadosDoPortal): ResultadosCalculadosDoPortal {
  const c = consolidacaoDoPortal(entrada)
  const completo = c.estado === 'completo'
  const semNumeros = { impressoes: null, cliques: null, ctrPercentual: null, resultadosDe: null, resultadosAte: null, atualizadoEm: null }
  if (entrada.vinculos.length === 0) {
    return { completo: false, parcial: false, resultados: { situacaoDosDados: 'Ainda sem dados', ...semNumeros, rotulo: ROTULO_DOS_RESULTADOS_NO_PORTAL } }
  }
  if (!completo || c.ate === null || entrada.atualizadoEm === null) {
    return { completo, parcial: !completo, resultados: { situacaoDosDados: 'Em atualização', ...semNumeros, rotulo: ROTULO_DOS_RESULTADOS_NO_PORTAL } }
  }
  const resumo = c.detalhe.resumo
  return {
    completo,
    parcial: false,
    resultados: {
      situacaoDosDados: 'Completo',
      impressoes: resumo.impressoes.toString(),
      cliques: resumo.cliques.toString(),
      ctrPercentual: ctrEmPercentualDv360(resumo.ctr, 2)?.toFixed(2) ?? null,
      resultadosDe: c.desde,
      resultadosAte: c.ate,
      atualizadoEm: entrada.atualizadoEm,
      rotulo: ROTULO_DOS_RESULTADOS_NO_PORTAL,
    },
  }
}

/**
 * O que o job do worker passa a `portal.fn_materializar_portal(jsonb)` por campanha, nos nomes das colunas do
 * `jsonb_to_recordset` da fonte (0200). A assinatura é a do MESMO retrato dos números: se o conjunto mudou entre o
 * cálculo e a gravação, a fonte grava "Em atualização".
 */
export const numerosDaCampanhaParaOPortal = z.object({
  campanha_id: z.string(),
  assinatura: z.string().min(1),
  completo: z.boolean(),
  ate: dataNominal.nullable(),
  desde: dataNominal.nullable(),
  impressoes: contagem.nullable(),
  cliques: contagem.nullable(),
  ctr_percentual: percentualComDuasCasas.nullable(),
  atualizado_em: z.string().nullable(),
})
export type NumerosDaCampanhaParaOPortal = z.infer<typeof numerosDaCampanhaParaOPortal>

export function numerosParaOMaterializador(campanhaId: string, entrada: EntradaDosResultadosDoPortal): NumerosDaCampanhaParaOPortal {
  const { completo, resultados } = resultadosDoPortalPelaConsolidacao(entrada)
  return {
    campanha_id: campanhaId,
    assinatura: entrada.assinatura,
    completo: completo && resultados.situacaoDosDados === 'Completo',
    ate: resultados.resultadosAte,
    desde: resultados.resultadosDe,
    impressoes: resultados.impressoes,
    cliques: resultados.cliques,
    ctr_percentual: resultados.ctrPercentual,
    atualizado_em: resultados.atualizadoEm,
  }
}

// ------------------------------------------------------------------------------------------------ o diálogo "Portal" (API)

/** O corpo de ligar e desligar: o público. */
export const interruptorDoPortal = z.object({ publico: publicoDoPortal })
export type InterruptorDoPortal = z.infer<typeof interruptorDoPortal>

/**
 * O cartão como o público o verá (os mesmos números para os três, §5.8), salvo o valor: o Parceiro comissionado
 * nunca o vê (P-48), e quem não vê os valores de Mídia recebe null. `situacao` é null só na Cancelada, que não vai ao
 * portal.
 */
export const cartaoDaCampanhaNoPortal = z.object({
  codigo: z.string(),
  refExterna: z.string().nullable(),
  nome: z.string(),
  inicio: dataNominal,
  fim: dataNominal,
  situacao: situacaoDaCampanhaNoPortal.nullable(),
  valor: z.string().regex(/^\d{1,16}(?:\.\d{1,2})?$/).nullable(),
})
export type CartaoDaCampanhaNoPortal = z.infer<typeof cartaoDaCampanhaNoPortal>

/**
 * Um público no diálogo:
 *   · `ligado`: o interruptor;
 *   · `empresas`: as empresas do pedido com este papel (vazio = o pedido não tem a parte, e não há o que ligar);
 *   · `podeLigar`: `midia · editar_todos`, a campanha admite ligar (§5.5: até a Concluída) e o pedido tem a parte;
 *   · `podeDesligar`: `midia · editar_todos` e o interruptor ligado (em qualquer status);
 *   · `motivo`: por que não pode ligar, para a linha do diálogo (null quando pode, ou quando já está ligado).
 */
export const publicoDaCampanhaNoPortal = z.object({
  publico: publicoDoPortal,
  ligado: z.boolean(),
  empresas: z.array(z.object({ id: uuid, nome: z.string() })),
  podeLigar: z.boolean(),
  podeDesligar: z.boolean(),
  motivo: z.enum(['sem_permissao', 'status', 'sem_parte']).nullable(),
  /** A prévia do cartão para este público (§5.3). */
  previa: z.object({ cartao: cartaoDaCampanhaNoPortal, resultados: resultadosDaCampanhaNoPortal }),
})
export type PublicoDaCampanhaNoPortal = z.infer<typeof publicoDaCampanhaNoPortal>

/**
 * Um vínculo como o diálogo o lista: o ID da Campaign, o estado da conferência pelo Google e quem criou o vínculo
 * (D-192): `vinculadoPor`, o mesmo nome do `vinculoDv360`. A palavra "confirmado" é só da conferência do Google (o
 * `estado`): o vínculo aguardando conferência nunca se apresenta como confirmado, nem por quem o criou — a tela diz
 * "vinculada por {nome}" (DS-684 (f), correção 4 da autorização).
 */
export const vinculoDaCampanhaNoPortal = z.object({
  campaignId: idDoGoogle,
  estado: z.enum(['aguardando_conferencia', 'confirmado', 'nao_encontrado']),
  vinculadoPor: z.object({ id: uuid, nome: z.string() }).strict(),
}).strict()
export type VinculoDaCampanhaNoPortal = z.infer<typeof vinculoDaCampanhaNoPortal>

export const portalDaCampanha = z.object({
  campanhaId: uuid,
  publicos: z.array(publicoDaCampanhaNoPortal).length(3),
  vinculos: z.array(vinculoDaCampanhaNoPortal),
  /** O aviso de consolidado parcial (§5.3): há vínculo e o consolidado não está completo. */
  parcial: z.boolean(),
  textoAoDesligar: z.literal(TEXTO_AO_DESLIGAR_A_PUBLICACAO),
})
export type PortalDaCampanha = z.infer<typeof portalDaCampanha>
